import React, { useRef, useState, useCallback, useEffect, lazy, Suspense } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  IconButton,
  FormHelperText,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  Button,
  Stack
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InfoIcon from '@mui/icons-material/Info';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TerrainIcon from '@mui/icons-material/Terrain';
import { useDropzone } from 'react-dropzone';

// Lazy load ElevationChart
const ElevationChart = lazy(() => import('../ElevationChart'));

// Prevent mouse wheel scrolling on number inputs
const preventWheelChange = (e) => {
  e.target.blur();
};

const RaceDetailsSection = ({ formData, onChange, onGPXUpload, errors = {} }) => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle direct input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange(name, value);
  };

  const handlePaceModelChange = (e) => {
    onChange('paceModel', e.target.value);
  };

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3958.8; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Process GPX file and extract data
  const processGpxFile = useCallback((file) => {
    console.log("Processing GPX file:", file.name);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const gpxContent = e.target.result;
      
      try {
        // Parse GPX file
        const parser = new DOMParser();
        const gpxDoc = parser.parseFromString(gpxContent, "text/xml");
        
        // Check if this is a valid GPX file
        const gpxElement = gpxDoc.getElementsByTagName("gpx")[0];
        if (!gpxElement) {
          console.error("Invalid GPX file format");
          alert("Invalid GPX file format");
          return;
        }
        
        // Get track points
        const trackPoints = gpxDoc.getElementsByTagName("trkpt");
        console.log(`Found ${trackPoints.length} track points`);
        
        if (trackPoints.length === 0) {
          console.error("No track points found in GPX file");
          alert("No track points found in GPX file");
          return;
        }
        
        // Extract data from track points
        const gpxData = [];
        const elevationData = [];
        let totalDistance = 0;
        let prevPoint = null;
        
        for (let i = 0; i < trackPoints.length; i++) {
          const point = trackPoints[i];
          const lat = parseFloat(point.getAttribute("lat"));
          const lon = parseFloat(point.getAttribute("lon"));
          const ele = point.getElementsByTagName("ele")[0]?.textContent;
          
          if (lat && lon) {
            // Create a point object
            const currentPoint = { lat, lon };
            
            // Calculate distance from previous point
            if (prevPoint) {
              const segmentDistance = calculateDistance(
                prevPoint.lat,
                prevPoint.lon,
                lat,
                lon
              );
              totalDistance += segmentDistance;
            }
            
            // Add to gpxData array
            gpxData.push({
              lat,
              lon,
              elevation: ele ? parseFloat(ele) : null,
              distance: totalDistance
            });
            
            // If we have elevation data, add to elevationData array
            if (ele) {
              // Convert elevation to meters if needed (using a heuristic)
              let elevation = parseFloat(ele);
              
              // If the elevation is very large (> 5000), it's likely in feet, so convert to meters
              if (elevation > 5000) {
                console.log(`Converting elevation from feet to meters: ${elevation} ft -> ${elevation / 3.28084} m`);
                elevation = elevation / 3.28084;
              }
              
              elevationData.push({
                distance: totalDistance,
                elevation: elevation,
                cumulativeDistance: totalDistance * 1609.34 // Haversine track length is miles → meters
              });
            }
            
            prevPoint = currentPoint;
          }
        }
        
        console.log(`Processed ${elevationData.length} elevation points`);
        console.log("Sample elevation data point:", elevationData.length > 0 ? elevationData[0] : "None");
        
        if (elevationData.length === 0) {
          console.error("No elevation data found in GPX file");
          alert("No elevation data found in GPX file");
          return;
        }
        
        // Set the form data for race distance (round to 1 decimal place)
        const raceDistance = Math.round(totalDistance * 10) / 10;
        
        // Call the onGPXUpload callback with the extracted data
        console.log("Calling onGPXUpload with:", {
          gpxDataLength: gpxData.length,
          elevationDataLength: elevationData.length,
          raceDistance
        });
        
        // Deep clone the arrays to avoid reference issues
        const finalElevationData = JSON.parse(JSON.stringify(elevationData));
        
        onGPXUpload(
          JSON.parse(JSON.stringify(gpxData)), 
          finalElevationData,
          raceDistance
        );
        
        // Also update the distance field
        onChange("distance", raceDistance);
        
        // Log the final result for debugging
        setTimeout(() => {
          console.log("GPX processing complete. Final elevation data length:", finalElevationData.length);
        }, 100);
        
      } catch (error) {
        console.error("Error processing GPX file:", error);
        alert("Error processing GPX file: " + error.message);
      }
    };
    
    reader.onerror = (error) => {
      console.error("Error reading GPX file:", error);
      alert("Error reading GPX file");
    };
    
    reader.readAsText(file);
  }, [onGPXUpload, onChange]);

  // Handle click on upload button
  const handleUploadClick = () => {
    // Simply trigger the file input click
    fileInputRef.current.click();
  };

  // Update the handleFileUpload function to use processGpxFile
  const handleFileUpload = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      console.log("File selected via input:", file.name);
      processGpxFile(file);
    }
  }, [processGpxFile]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (acceptedFiles) => {
      console.log("File dropped in handleDrop:", acceptedFiles);
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        console.log("Processing dropped file:", file.name);
        processGpxFile(file);
      }
    },
    [processGpxFile]
  );

  // Update the Dropzone implementation
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      'application/gpx+xml': ['.gpx'],
      'text/xml': ['.gpx']
    },
    multiple: false
  });

  // Force a component re-render when elevation data changes
  const [, forceUpdate] = useState({});
  useEffect(() => {
    if (formData.elevationData && formData.elevationData.length > 0) {
      console.log("Elevation data changed, forcing re-render", formData.elevationData.length);
      forceUpdate({});
      
      // Force another update after a slight delay
      setTimeout(() => {
        console.log("Forcing delayed re-render for elevation data");
        forceUpdate({});
      }, 100);
    }
  }, [formData.elevationData]);

  // Troubleshooting to display elevation data on screen
  useEffect(() => {
    if (formData.elevationData) {
      console.log("Elevation data in component:", 
        formData.elevationData.length, 
        "points, first point:", 
        formData.elevationData[0]
      );
    }
  }, [formData.elevationData]);

  return (
    <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
      
      <Paper variant="outlined" sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: 3, 
        borderRadius: 2,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider'
      }}>
        {/* Section Header */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'rgba(44, 95, 79, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DirectionsRunIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Race Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Basic information about your race
            </Typography>
          </Box>
        </Stack>
        <Grid container spacing={3}>
          {/* Race Name field */}
          <Grid item xs={12} md={6}>
            <TextField 
              fullWidth 
              label="Race Name" 
              name="raceName" 
              value={formData.raceName}
              onChange={handleInputChange}
              placeholder="e.g., Western States 100"
              variant="outlined"
              required
              error={!!errors.raceName}
              helperText={errors.raceName}
            />
          </Grid>
          
          {/* Race Date field */}
          <Grid item xs={12} md={6}>
            <TextField 
              fullWidth 
              label="Race Date" 
              name="raceDate" 
              type="date"
              value={formData.raceDate || ''}
              onChange={handleInputChange}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Optional - helps with planning and organizing your races"
            />
          </Grid>
          
          {/* Distance and unit fields */}
          <Grid item xs={7} md={3}>
            <TextField
              fullWidth
              id="distance"
              name="distance"
              label="Distance"
              value={formData.distance}
              onChange={handleInputChange}
              error={!!errors.distance}
              helperText={errors.distance}
              variant="outlined"
              required
              type="text"
              InputProps={{
                inputProps: {
                  inputMode: "numeric",
                  pattern: "[0-9]*[.][0-9]*",
                }
              }}
            />
          </Grid>
          
          <Grid item xs={5} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Unit</InputLabel>
              <Select
                name="distanceUnit"
                value={formData.distanceUnit}
                onChange={handleInputChange}
                label="Unit"
              >
                <MenuItem value="miles">Miles</MenuItem>
                <MenuItem value="kilometers">Kilometers</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Race Time Details Section */}
      <Paper variant="outlined" sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: 3, 
        borderRadius: 2,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider'
      }}>
        {/* Section Header */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'rgba(44, 95, 79, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Time Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Start time, goal, and cutoff
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 3, md: 4 } }}>
          {/* Race Start Time */}
          <Box sx={{ flex: '1 1 0' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight={500} color="text.secondary">
              Race Start Time
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                id="startTimeHours"
                name="startTimeHours"
                label="Hour"
                value={formData.startTimeHours || ''}
                onChange={handleInputChange}
                variant="outlined"
                type="text"
                size="small"
                InputProps={{
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    min: 1,
                    max: 12
                  }
                }}
                sx={{ width: '80px' }}
              />
              <TextField
                id="startTimeMinutes"
                name="startTimeMinutes"
                label="Min"
                value={formData.startTimeMinutes || ''}
                onChange={handleInputChange}
                variant="outlined"
                type="text"
                size="small"
                InputProps={{
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    min: 0,
                    max: 59
                  }
                }}
                sx={{ width: '70px' }}
              />
              <FormControl variant="outlined" size="small" sx={{ width: '90px' }}>
                <InputLabel>AM/PM</InputLabel>
                <Select
                  name="startTimeAMPM"
                  value={formData.startTimeAMPM}
                  onChange={handleInputChange}
                  label="AM/PM"
                >
                  <MenuItem value="AM">AM</MenuItem>
                  <MenuItem value="PM">PM</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          {/* Cutoff Time */}
          <Box sx={{ flex: '1 1 0' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight={500} color="text.secondary">
              Cutoff Time
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                id="cutoffTimeHours"
                name="cutoffTimeHours"
                label="Hours"
                value={formData.cutoffTimeHours || ''}
                onChange={handleInputChange}
                variant="outlined"
                type="text"
                size="small"
                InputProps={{
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }
                }}
                sx={{ width: '80px' }}
              />
              <TextField
                id="cutoffTimeMinutes"
                name="cutoffTimeMinutes"
                label="Min"
                value={formData.cutoffTimeMinutes || ''}
                onChange={handleInputChange}
                variant="outlined"
                type="text"
                size="small"
                InputProps={{
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    min: 0,
                    max: 59
                  }
                }}
                sx={{ width: '70px' }}
              />
            </Box>
          </Box>
          
          {/* Goal Time */}
          <Box sx={{ flex: '1 1 0' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight={500} color="text.secondary">
              Goal Time (Optional)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                id="goalTimeHours"
                name="goalTimeHours"
                label="Hours"
                value={formData.goalTimeHours || ''}
                onChange={handleInputChange}
                variant="outlined"
                type="text"
                size="small"
                InputProps={{
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }
                }}
                sx={{ width: '80px' }}
              />
              <TextField
                id="goalTimeMinutes"
                name="goalTimeMinutes"
                label="Min"
                value={formData.goalTimeMinutes || ''}
                onChange={handleInputChange}
                variant="outlined"
                type="text"
                size="small"
                InputProps={{
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    min: 0,
                    max: 59
                  }
                }}
                sx={{ width: '70px' }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>
      
      {/* GPX File Upload Section */}
      <Paper variant="outlined" sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: 3, 
        borderRadius: 2,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider'
      }}>
        {/* Section Header */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'rgba(44, 95, 79, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TerrainIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Course Elevation
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload a GPX file for elevation profile
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".gpx"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{
                py: 1,
                width: { xs: '100%', sm: 'auto' },
                margin: '0 auto',
                display: 'flex',
                transition: 'all 0.2s ease',
                borderStyle: isDragActive ? 'dashed' : 'solid',
                borderColor: isDragActive ? 'primary.main' : undefined,
                boxShadow: isDragActive ? 1 : 0
              }}
            >
              {formData.elevationData ? 'GPX file uploaded' : 'Upload GPX file'}
            </Button>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              {formData.elevationData 
                ? 'Click to change file or drag & drop a new one' 
                : 'Upload a GPX file to automatically set distance and show elevation profile'}
            </Typography>
          </div>
        </Box>

        <Divider sx={{ my: 3 }} />

        <FormControl fullWidth variant="outlined">
          <InputLabel id="pace-model-label">Segment pacing from elevation</InputLabel>
          <Select
            labelId="pace-model-label"
            id="paceModel"
            value={formData.paceModel || 'even'}
            onChange={handlePaceModelChange}
            label="Segment pacing from elevation"
          >
            <MenuItem value="even">Even effort — ignore hills between aids (flat pace per segment before scaling)</MenuItem>
            <MenuItem value="terrain" disabled={!formData.elevationData?.length}>
              Terrain-adjusted — slower on climbs, faster on downhills (uses GPX)
            </MenuItem>
            <MenuItem value="simulation" disabled={!formData.elevationData?.length}>
              Conservative terrain — steeper uphills, smaller downhill boost (long race / leg fatigue)
            </MenuItem>
          </Select>
          <FormHelperText>
            {formData.elevationData?.length
              ? 'Your goal or cutoff time is unchanged; segment paces vary by grade, then rescale so total moving time still matches.'
              : 'Upload a GPX with elevation to enable terrain-based segment pacing.'}
          </FormHelperText>
        </FormControl>
      </Paper>
    </Box>
  );
};

export default RaceDetailsSection; 